import { useState, useRef, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'

const HIGHLIGHT_DURATION = 3000

/**
 * Hook to manage lead highlighting in ECG visualization
 * Handles visual feedback when clicking diagnosis cards
 */
export const useLeadHighlight = () => {
  const theme = useTheme()
  const [selectedLeads, setSelectedLeads] = useState<string[]>([])
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const HIGHLIGHT_STYLE = {
    border: `3px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 10px ${theme.palette.primary.main}50` // 50 = 31% opacity in hex
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const clearLeadStyles = (leads: string[]) => {
    for (const lead of leads) {
      const normalizedLead = lead.toUpperCase()
      const leadElements = document.querySelectorAll(
        `[data-lead="${normalizedLead}"]`
      )
      for (const el of Array.from(leadElements)) {
        ;(el as HTMLElement).style.border = ''
        ;(el as HTMLElement).style.boxShadow = ''
      }
    }
  }

  const applyLeadStyles = (leads: string[]) => {
    for (const lead of leads) {
      const normalizedLead = lead.toUpperCase()
      const leadElements = document.querySelectorAll(
        `[data-lead="${normalizedLead}"]`
      )
      if (import.meta.env.DEV && leadElements.length === 0) {
        console.warn(
          `[ECG Highlight] Lead not found in DOM: ${normalizedLead} (original: ${lead})`
        )
      }
      for (const el of Array.from(leadElements)) {
        ;(el as HTMLElement).style.border = HIGHLIGHT_STYLE.border
        ;(el as HTMLElement).style.boxShadow = HIGHLIGHT_STYLE.boxShadow
      }
    }
  }

  const scrollToLead = (lead: string) => {
    const normalizedLead = lead.toUpperCase()
    const leadElement = document.querySelector(
      `[data-lead="${normalizedLead}"]`
    )
    if (leadElement) {
      leadElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }

  const highlightLeads = (leads: string[]) => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Clear previous highlights
    clearLeadStyles(selectedLeads)

    // Apply new highlights
    setSelectedLeads(leads)
    applyLeadStyles(leads)

    // Scroll to first lead
    if (leads.length > 0) {
      scrollToLead(leads[0])
    }

    // Auto-remove highlights after duration
    timeoutRef.current = setTimeout(() => {
      clearLeadStyles(leads)
      setSelectedLeads([])
    }, HIGHLIGHT_DURATION)
  }

  return { highlightLeads, selectedLeads }
}
